import { Card, Group, Stack, Text } from '@mantine/core'
import { IconArrowDownRight, IconArrowUpRight, IconRobot } from '@tabler/icons-react'
import React from 'react'

interface StatsCardProps {
  title: string
  value?: string | number
  icon: React.ComponentType<any>
  color?: string
  metricKey?: string
  subtitle?: string
  dualMetric?: { total: number; ai: number }
  dualMetricLabels?: { left: string; right: string }
  dualMetricRightColor?: string
  dualMetricLeftAiVisual?: boolean
  dualMetricRightAiVisual?: boolean
  tripleMetric?: { left: number; middle: number; right: number }
  tripleMetricLabels?: { left: string; middle: string; right: string }
  tripleMetricRightAiVisual?: boolean
  trend?: {
    value: number
    label: string
  }
}

export function StatsCard({
  title,
  value,
  color = 'blue',
  trend,
  subtitle,
  dualMetric,
  dualMetricLabels,
  dualMetricRightColor = 'violet',
  dualMetricLeftAiVisual = false,
  dualMetricRightAiVisual = false,
  tripleMetric,
  tripleMetricLabels,
  tripleMetricRightAiVisual = false,
}: StatsCardProps) {
  const isPositiveTrend = trend && trend.value > 0
  const labeledDual = dualMetric && dualMetricLabels ? dualMetricLabels : null
  const labeledTriple = tripleMetric && tripleMetricLabels ? tripleMetricLabels : null

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="sm"
      withBorder
      style={{
        borderBottom: `4px solid var(--mantine-color-${color}-6)`,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'visible',
        minHeight: tripleMetric ? '120px' : dualMetric ? '112px' : '100px',
        display: 'flex',
        flexDirection: 'column',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
          },
        },
      }}
    >
      <Stack gap="sm" style={{ flex: 1, justifyContent: 'center' }} align="center">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.5px' }} ta="center" w="100%">
          {title}
        </Text>

        {tripleMetric && labeledTriple ? (
          <Group gap="sm" align="flex-end" justify="center" wrap="nowrap" w="100%">
            <Stack gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" c="dimmed" fw={600} ta="center" lineClamp={2}>
                {labeledTriple.left}
              </Text>
              <Text size="26px" fw={700} style={{ lineHeight: 1 }}>
                {tripleMetric.left}
              </Text>
            </Stack>
            <Text size="xl" c="dimmed" fw={300} pb={6} style={{ flexShrink: 0 }}>
              |
            </Text>
            <Stack gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" c="dimmed" fw={600} ta="center" lineClamp={2}>
                {labeledTriple.middle}
              </Text>
              <Text size="26px" fw={700} style={{ lineHeight: 1 }}>
                {tripleMetric.middle}
              </Text>
            </Stack>
            <Text size="xl" c="dimmed" fw={300} pb={6} style={{ flexShrink: 0 }}>
              |
            </Text>
            <Stack gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
              {tripleMetricRightAiVisual ? (
                <Group gap={4} wrap="nowrap" justify="center">
                  <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                  <Text size="xs" c="violet" fw={600} ta="center" lineClamp={2}>
                    {labeledTriple.right}
                  </Text>
                </Group>
              ) : (
                <Text size="xs" c="dimmed" fw={600} ta="center" lineClamp={2}>
                  {labeledTriple.right}
                </Text>
              )}
              <Text size="26px" fw={700} c={tripleMetricRightAiVisual ? 'violet' : undefined} style={{ lineHeight: 1 }}>
                {tripleMetric.right}
              </Text>
            </Stack>
          </Group>
        ) : dualMetric ? (
          <Group gap="lg" align="flex-end" justify="center" wrap="nowrap" w="100%">
            <Stack gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
              {labeledDual && dualMetricLeftAiVisual ? (
                <Group gap={4} wrap="nowrap" justify="center">
                  <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                  <Text size="xs" c="violet" fw={600} ta="center" lineClamp={2}>
                    {labeledDual.left}
                  </Text>
                </Group>
              ) : (
                <Text size="xs" c="dimmed" fw={600} ta="center" lineClamp={2}>
                  {labeledDual ? labeledDual.left : 'Total'}
                </Text>
              )}
              <Text
                size="28px"
                fw={700}
                c={labeledDual && dualMetricLeftAiVisual ? 'violet' : undefined}
                style={{ lineHeight: 1 }}
              >
                {dualMetric.total}
              </Text>
            </Stack>
            <Text size="xl" c="dimmed" fw={300} pb={6} style={{ flexShrink: 0 }}>
              |
            </Text>
            <Stack gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
              {labeledDual && dualMetricRightAiVisual ? (
                <Group gap={4} wrap="nowrap" justify="center">
                  <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                  <Text size="xs" c="violet" fw={600} ta="center" lineClamp={2}>
                    {labeledDual.right}
                  </Text>
                </Group>
              ) : labeledDual ? (
                <Text size="xs" c={dualMetricLeftAiVisual ? 'dimmed' : dualMetricRightColor} fw={600} ta="center" lineClamp={2}>
                  {labeledDual.right}
                </Text>
              ) : (
                <Group gap={4} wrap="nowrap" justify="center">
                  <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                  <Text size="xs" c="violet" fw={600}>
                    AI
                  </Text>
                </Group>
              )}
              <Text
                size="28px"
                fw={700}
                c={
                  !labeledDual
                    ? 'violet'
                    : dualMetricRightAiVisual
                      ? 'violet'
                      : dualMetricLeftAiVisual
                        ? undefined
                        : dualMetricRightColor
                }
                style={{ lineHeight: 1 }}
              >
                {dualMetric.ai}
              </Text>
            </Stack>
          </Group>
        ) : (
          <>
            <Group gap="xs" align="center" justify="center" wrap="nowrap" w="100%">
              {trend &&
                (isPositiveTrend ? (
                  <IconArrowUpRight size={20} color="var(--mantine-color-teal-6)" />
                ) : (
                  <IconArrowDownRight size={20} color="var(--mantine-color-red-6)" />
                ))}
              <Text size="32px" fw={700} style={{ lineHeight: 1 }} ta="center">
                {value}
              </Text>
            </Group>
            {subtitle ? (
              <Text size="xs" c="dimmed" lineClamp={2} ta="center" w="100%">
                {subtitle}
              </Text>
            ) : null}
          </>
        )}
      </Stack>
    </Card>
  )
}
